const BLOCKED_HOSTNAMES = new Set([
  "localhost",
  "metadata.google.internal",
  "metadata.goog",
]);

function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/^\[|\]$/g, "");
}

function isBlockedIpv4(host: string): boolean {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!match) {
    return false;
  }

  const octets = match.slice(1, 5).map((part) => Number(part));
  if (octets.some((octet) => Number.isNaN(octet) || octet < 0 || octet > 255)) {
    return true;
  }

  const [a, b, _c, _d] = octets;
  if (a === undefined || b === undefined) {
    return true;
  }
  if (a === 127 || a === 0) return true;
  if (a === 10) return true;
  if (a === 169 && b === 254) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;

  return false;
}

function ipv4FromNumericHost(host: string): string | null {
  if (/^\d+$/.test(host)) {
    const num = Number(host);
    if (!Number.isFinite(num) || num < 0 || num > 0xffffffff) {
      return null;
    }
    const a = (num >>> 24) & 0xff;
    const b = (num >>> 16) & 0xff;
    const c = (num >>> 8) & 0xff;
    const d = num & 0xff;
    return `${a}.${b}.${c}.${d}`;
  }

  const hexMatch = /^0x([0-9a-f]{1,8})$/i.exec(host);
  if (hexMatch) {
    const num = Number.parseInt(hexMatch[1]!, 16);
    if (!Number.isFinite(num) || num < 0 || num > 0xffffffff) {
      return null;
    }
    const a = (num >>> 24) & 0xff;
    const b = (num >>> 16) & 0xff;
    const c = (num >>> 8) & 0xff;
    const d = num & 0xff;
    return `${a}.${b}.${c}.${d}`;
  }

  return null;
}

function isBlockedIpv4Mapped(host: string): boolean {
  const mappedDotted = /^::ffff:(\d{1,3}(?:\.\d{1,3}){3})$/i.exec(host);
  if (mappedDotted) {
    return isBlockedIpv4(mappedDotted[1]!);
  }

  const mappedHex = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/i.exec(host);
  if (mappedHex) {
    const high = Number.parseInt(mappedHex[1]!, 16);
    const low = Number.parseInt(mappedHex[2]!, 16);
    if (Number.isFinite(high) && Number.isFinite(low)) {
      const a = (high >> 8) & 0xff;
      const b = high & 0xff;
      const c = (low >> 8) & 0xff;
      const d = low & 0xff;
      return isBlockedIpv4(`${a}.${b}.${c}.${d}`);
    }
  }

  return false;
}

function isBlockedIpv6(host: string): boolean {
  if (host === "::1" || host === "0:0:0:0:0:0:0:1") {
    return true;
  }
  if (host.startsWith("fc") || host.startsWith("fd")) {
    return true;
  }
  if (host.startsWith("fe80:")) {
    return true;
  }
  if (isBlockedIpv4Mapped(host)) {
    return true;
  }
  return false;
}

function isBlockedHostname(hostname: string): boolean {
  const host = normalizeHostname(hostname);
  if (!host) {
    return true;
  }
  if (BLOCKED_HOSTNAMES.has(host)) {
    return true;
  }
  if (host.endsWith(".localhost") || host.endsWith(".local")) {
    return true;
  }

  const numericIpv4 = ipv4FromNumericHost(host);
  if (numericIpv4 && isBlockedIpv4(numericIpv4)) {
    return true;
  }

  return isBlockedIpv4(host) || isBlockedIpv6(host);
}

/**
 * Returns true when the URL uses http(s) and targets a public, non-reserved host.
 */
export function isPublicHttpUrl(urlString: string): boolean {
  let url: URL;
  try {
    url = new URL(urlString);
  } catch {
    return false;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return false;
  }
  if (url.username || url.password) {
    return false;
  }

  return !isBlockedHostname(url.hostname);
}

export const PUBLIC_HTTP_URL_ERROR =
  "URL must be a public http:// or https:// address";

const FORBIDDEN_HANDLER_HEADERS = new Set([
  "host",
  "connection",
  "content-length",
  "transfer-encoding",
  "te",
  "trailer",
  "upgrade",
  "proxy-authorization",
  "proxy-connection",
  "keep-alive",
  "x-robotrock-signature",
  "x-robotrock-delivery-id",
  "x-robotrock-delivery-attempt",
]);

/**
 * Allow only safe custom webhook headers; blocks overrides of delivery metadata.
 */
export function filterHandlerHeaders(
  headers: Record<string, string> | undefined
): Record<string, string> {
  if (!headers) {
    return {};
  }

  const filtered: Record<string, string> = {};
  for (const [rawKey, value] of Object.entries(headers)) {
    const key = rawKey.toLowerCase().trim();
    if (!key || FORBIDDEN_HANDLER_HEADERS.has(key)) {
      continue;
    }
    if (!/^[a-z0-9][a-z0-9._-]*$/.test(key)) {
      continue;
    }
    filtered[key] = value;
  }
  return filtered;
}
